import type { FieldDef, FieldOption, FormSettings, FormType, IqSpecFields } from '@jfh/intake-shared';
import {
  ADDRESS_COMPONENT_LABELS,
  iqFieldOptions,
  isCountryQuestion,
  iqMappingKeyFor,
  iqTargetOf,
  resolveAddressComponents,
  resolveStaticOptions,
  suggestIqField,
} from '@jfh/intake-shared';

// Non-component helpers for the question panel (kept apart from the
// components so React fast refresh works).

/** Format-check presets (the same patterns the old panel stored). */
export const FORMAT_PRESETS: Record<string, string> = {
  email: '^[^@\\s]+@[^@\\s]+\\.[^@\\s]+$',
  phone: '^(?:0|\\+44)\\d{9,10}$',
  vat: '^GB\\d{9}$',
};

export type FormatKind = 'none' | 'email' | 'phone' | 'vat' | 'maxLength' | 'regex';

export function formatKind(f: FieldDef): FormatKind {
  const v = f.validation;
  if (v?.pattern !== undefined) {
    return (Object.keys(FORMAT_PRESETS).find((k) => FORMAT_PRESETS[k] === v.pattern) as FormatKind | undefined) ?? 'regex';
  }
  return typeof v?.maxLength === 'number' ? 'maxLength' : 'none';
}

/** A question (or address part / country attribute) a rule can depend on. */
export interface RuleCandidate {
  id: string;
  label: string;
  /** Pick-one answers → pills for "is" / "is not". */
  options?: FieldOption[];
  /** Several answers can be ticked → "includes" one of them. */
  multi?: boolean;
}

const MULTI_TYPES = new Set(['multiselect']);

/**
 * Everything a question's rule can depend on: the form's other questions,
 * address parts, and the country's lookup attributes (e.g. "is in the EU"),
 * using the same dotted ids the form stores.
 */
export function ruleCandidates(
  field: FieldDef,
  fields: FieldDef[],
  countryAttrs: string[],
  /** The values each country attribute takes in iQ's list, offered as answers. */
  attrValues: Record<string, string[]> = {},
  /** iQ lists already loaded, by lookup key: their answers are offered too. */
  iqLists: Record<string, FieldOption[]> = {},
): RuleCandidate[] {
  /** "is in the EU" style conditions on a question's (or address's) country. */
  const attrCandidates = (id: string, label: string): RuleCandidate[] =>
    countryAttrs.map((attr) => {
      const values = attrValues[attr] ?? [];
      return {
        id: `${id}.country.${attr}`,
        label: `${label}: country ${attr === 'D_EUCountry' ? 'is in the EU' : attr}`,
        ...(values.length ? { options: values.map((v) => ({ value: v, label: v })) } : {}),
      };
    });
  const out: RuleCandidate[] = [];
  // A step can only depend on answers from the steps before it.
  const at = fields.findIndex((x) => x.id === field.id);
  const pool = field.type === 'heading' && at >= 0 ? fields.slice(0, at).filter((x) => x.audience === 'customer') : fields;
  for (const x of pool) {
    if (x.id === field.id || x.type === 'heading') continue;
    if (x.type === 'address') {
      for (const c of resolveAddressComponents(x).filter((cc) => cc.enabled)) {
        const part = (c.label?.trim() || ADDRESS_COMPONENT_LABELS[c.key]).toLowerCase();
        out.push({ id: `${x.id}.${c.key}`, label: `${x.label}: ${part}` });
        if (c.key === 'country') out.push(...attrCandidates(x.id, x.label));
      }
      continue;
    }
    const staticChoices = !x.optionsSource ? resolveStaticOptions(x) : (iqLists[x.optionsSource.key] ?? []);
    if (x.type === 'checkbox' && !staticChoices.length) {
      out.push({ id: x.id, label: x.label, options: [{ value: 'true', label: 'Ticked' }] });
    } else if (['select', 'radio', 'multiselect', 'checkbox'].includes(x.type) && staticChoices.length) {
      out.push({
        id: x.id,
        label: x.label,
        options: staticChoices,
        multi: MULTI_TYPES.has(x.type) || x.type === 'checkbox',
      });
    } else {
      out.push({ id: x.id, label: x.label });
    }
    if (isCountryQuestion(x)) out.push(...attrCandidates(x.id, x.label));
  }
  return out;
}

/** One-line summary of what More options has set. */
export function moreSummary(f: FieldDef): string {
  const bits: string[] = [];
  if (f.type !== 'heading') bits.push(f.audience === 'staff' ? 'Answered by staff' : 'Answered by the applicant');
  if (f.defaultValue) bits.push(`starts as “${f.defaultValue}”`);
  if (f.defaultWhen) bits.push('starting answer depends on another');
  if (formatKind(f) !== 'none') bits.push('format checked');
  if (f.cardSummary) bits.push('on the Accounts card');
  if (f.choices) bits.push('stores different values');
  const s = bits.join(', ');
  return s ? s[0]!.toUpperCase() + s.slice(1) : 'Nothing else to set';
}

/** One-line summary of where the answer goes. */
export function connectionsSummary(
  f: FieldDef,
  formType: FormType,
  settings: FormSettings,
  liveFields?: IqSpecFields | null,
): string {
  const key = iqMappingKeyFor(f, formType);
  const target = iqTargetOf(settings, key, f.id);
  if (target) return `Sent to iQ as ${target}`;
  const suggestion = suggestIqField(f.label, iqFieldOptions(formType, settings, liveFields));
  return suggestion ? `Not sent to iQ yet · suggested: ${suggestion}` : 'Not sent to iQ';
}

/**
 * Questions an address can take its country from: the applicant's dropdowns
 * and choice buttons, those using the iQ country list first (their answers
 * are country codes, which is what the Country line stores).
 */
export function countrySources(fields: FieldDef[]): { id: string; label: string; iqCountries: boolean }[] {
  return fields
    .filter((x) => x.audience === 'customer' && (x.type === 'select' || x.type === 'radio'))
    .map((x) => ({ id: x.id, label: x.label, iqCountries: x.optionsSource?.key === 'countries' }))
    .sort((a, b) => Number(b.iqCountries) - Number(a.iqCountries));
}

/** Questions a contact line can start as: the applicant's one-line text and choice answers. */
export function subDefaultSources(fields: FieldDef[]): FieldDef[] {
  return fields.filter(
    (x) => x.audience === 'customer' && ['short_text', 'email', 'phone', 'select', 'radio'].includes(x.type),
  );
}
